import { pageAcceptsRedirect } from "@/pageRoutes";
import {
    responseStatus,
    serverRedirect,
    shouldShowErrorPage,
} from "@/utils/apiErrors";
import { navigateTo } from "@/utils/navigation";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

const HANDLED_STATUSES = [401, 409, 422, 429];

function isCurrentPage(url: string) {
    const target = new URL(url, window.location.origin);

    return (
        target.pathname === window.location.pathname &&
        (target.search === "" || target.search === window.location.search)
    );
}

/*
 * A page that sends the user back once they are done (login, email
 * verification) is told where they were through its ?redirect parameter.
 */
function withReturnPath(url: string) {
    const target = new URL(url, window.location.origin);
    if (
        !pageAcceptsRedirect(target.pathname) ||
        target.searchParams.has("redirect")
    ) {
        return url;
    }
    target.searchParams.set(
        "redirect",
        window.location.pathname + window.location.search,
    );

    return target.toString();
}

/*
 * Guards answer API calls that cannot go on with `409 {redirect}` (see
 * App\Http\Responses\SpaRedirect), so any query or mutation that gets one
 * takes the user there, unless they are already on that page. Every other
 * failure the app does not handle on purpose is reported, including ones a
 * component swallows with throwOnError: false; Sentry drops the duplicate when
 * an error boundary reports the same error.
 */
function handleError(error: Error) {
    const redirect = serverRedirect(error);
    if (redirect && !isCurrentPage(redirect)) {
        void navigateTo(withReturnPath(redirect), { replace: true });
    }

    const status = responseStatus(error);
    if (!status || !HANDLED_STATUSES.includes(status)) {
        Sentry.captureException(error);
    }
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: handleError }),
    mutationCache: new MutationCache({ onError: handleError }),
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
            throwOnError: (error) => shouldShowErrorPage(error),
        },
        mutations: {
            retry: false,
            throwOnError: (error) => shouldShowErrorPage(error),
        },
    },
});
